import type { CrudFieldConfig } from "components/admin/workspace-crud";

import { toSelectOptions } from "pages/app/workspace/workspace-helpers";

export const buildEventFields = (athletes: any[]): CrudFieldConfig[] => [
  {
    name: "player_id",
    label: "Athlete",
    kind: "select",
    options: toSelectOptions(athletes, (athlete) => athlete.full_name || "Unknown"),
    required: true,
  },
  {
    name: "name",
    label: "Event name",
    placeholder: "Fan meet-up, media day, clinic",
    required: true,
  },
  {
    name: "date",
    label: "Date",
    kind: "date",
    required: true,
  },
  {
    name: "location",
    label: "Location",
    placeholder: "Johannesburg, Cape Town, Nairobi",
  },
  {
    name: "details",
    label: "Details",
    kind: "textarea",
    placeholder: "Share the key details fans or staff should know.",
  },
];

export const buildEventCreateValues = (defaultPlayerId: string) => ({
  player_id: defaultPlayerId,
  name: "",
  date: new Date().toISOString().slice(0, 10),
  location: "",
  details: "",
});

export const buildEventUpdateValues = (currentUser: any, row: any) => ({
  player_id: row.player_id || row.player?.id || currentUser?.player?.id || "",
  name: row.name || "",
  date: row.date || "",
  location: row.location || "",
  details: row.details || "",
});

export const buildPlayerClassFields = (athletes: any[]): CrudFieldConfig[] => [
  {
    name: "player_id",
    label: "Athlete",
    kind: "select",
    options: toSelectOptions(athletes, (athlete) => athlete.full_name || "Unknown"),
    required: true,
  },
  {
    name: "title",
    label: "Class title",
    placeholder: "Masterclass, skills breakdown, recovery session",
    required: true,
  },
  {
    name: "video_url",
    label: "Video URL",
    placeholder: "https://videos.athspire.app/example-class",
    required: true,
  },
  {
    name: "price",
    label: "Price",
    kind: "number",
    min: 0,
    step: "0.01",
    required: true,
  },
];

export const buildPlayerClassCreateValues = (defaultPlayerId: string) => ({
  player_id: defaultPlayerId,
  title: "",
  video_url: "",
  price: 0,
});

export const buildPlayerClassUpdateValues = (currentUser: any, row: any) => ({
  player_id: row.player_id || row.player?.id || currentUser?.player?.id || "",
  title: row.title || "",
  video_url: row.video_url || "",
  price: row.price ?? 0,
});

export const buildPlayerProfileFields = (teams: any[]): CrudFieldConfig[] => [
  {
    name: "first_name",
    label: "First name",
    required: true,
  },
  {
    name: "last_name",
    label: "Last name",
    required: true,
  },
  {
    name: "position",
    label: "Position",
    placeholder: "Opening batter, wing, striker",
  },
  {
    name: "team_id",
    label: "Primary team",
    kind: "select",
    options: toSelectOptions(
      teams,
      (team) =>
        `${team.name || "Unnamed team"}${team.sport_name || team.sport ? ` | ${team.sport_name || team.sport}` : ""}`,
    ),
  },
];

export const buildPlayerCareerFields = ({
  teams,
  sports,
  countries,
}: {
  teams: any[];
  sports: any[];
  countries: any[];
}): CrudFieldConfig[] => [
  {
    name: "team_id",
    label: "Team",
    kind: "select",
    options: toSelectOptions(teams, (team) => team.name || "Unnamed team"),
  },
  {
    name: "sport_id",
    label: "Sport",
    kind: "select",
    options: toSelectOptions(sports, (sport) => sport.name || "Unknown sport"),
  },
  {
    name: "country_id",
    label: "Country",
    kind: "select",
    options: toSelectOptions(countries, (country) => country.name || "Unknown country"),
  },
  {
    name: "league_name",
    label: "League",
    placeholder: "Premier League, Varsity Cup",
  },
  {
    name: "start_year",
    label: "Start year",
    kind: "number",
    min: 1900,
  },
  {
    name: "end_year",
    label: "End year",
    kind: "number",
    min: 1900,
  },
  {
    name: "contract_type",
    label: "Contract type",
    placeholder: "Professional, Academy, Loan",
  },
  {
    name: "notes",
    label: "Notes",
    kind: "textarea",
    placeholder: "Add context for this career entry.",
  },
];

export const buildPlayerCareerCreateValues = () => ({
  team_id: "",
  sport_id: "",
  country_id: "",
  league_name: "",
  start_year: "",
  end_year: "",
  contract_type: "",
  notes: "",
});

export const buildPlayerCareerUpdateValues = (row: any) => ({
  team_id: row.team_id || "",
  sport_id: row.sport_id || "",
  country_id: row.country_id || "",
  league_name: row.league_name || "",
  start_year: row.start_year || "",
  end_year: row.end_year || "",
  contract_type: row.contract_type || "",
  notes: row.notes || "",
});

export const buildPlayerSeasonStatFields = ({
  teams,
  sports,
}: {
  teams: any[];
  sports: any[];
}): CrudFieldConfig[] => [
  {
    name: "team_id",
    label: "Team",
    kind: "select",
    options: toSelectOptions(teams, (team) => team.name || "Unnamed team"),
  },
  {
    name: "sport_id",
    label: "Sport",
    kind: "select",
    options: toSelectOptions(sports, (sport) => sport.name || "Unknown sport"),
  },
  {
    name: "season",
    label: "Season",
    placeholder: "2025/26",
    required: true,
  },
  {
    name: "tournament",
    label: "Tournament",
    placeholder: "League, Cup, Invitational",
  },
  {
    name: "matches_played",
    label: "Matches played",
    kind: "number",
    min: 0,
  },
  {
    name: "wins",
    label: "Wins",
    kind: "number",
    min: 0,
  },
  {
    name: "draws",
    label: "Draws",
    kind: "number",
    min: 0,
  },
  {
    name: "losses",
    label: "Losses",
    kind: "number",
    min: 0,
  },
  {
    name: "points",
    label: "Points",
    kind: "number",
    min: 0,
  },
];

export const buildPlayerSeasonStatCreateValues = () => ({
  team_id: "",
  sport_id: "",
  season: "",
  tournament: "",
  matches_played: 0,
  wins: 0,
  draws: 0,
  losses: 0,
  points: 0,
});

export const buildPlayerSeasonStatUpdateValues = (row: any) => ({
  team_id: row.team_id || "",
  sport_id: row.sport_id || "",
  season: row.season || "",
  tournament: row.tournament || "",
  matches_played: row.matches_played ?? 0,
  wins: row.wins ?? 0,
  draws: row.draws ?? 0,
  losses: row.losses ?? 0,
  points: row.points ?? 0,
});

export const buildPlayerPositionPlayedFields = ({
  teams,
  sports,
}: {
  teams: any[];
  sports: any[];
}): CrudFieldConfig[] => [
  {
    name: "team_id",
    label: "Team",
    kind: "select",
    options: toSelectOptions(teams, (team) => team.name || "Unnamed team"),
  },
  {
    name: "sport_id",
    label: "Sport",
    kind: "select",
    options: toSelectOptions(sports, (sport) => sport.name || "Unknown sport"),
  },
  {
    name: "season",
    label: "Season",
    placeholder: "2025/26",
  },
  {
    name: "position",
    label: "Position",
    placeholder: "Wing, Goalkeeper, Striker",
    required: true,
  },
  {
    name: "appearances",
    label: "Appearances",
    kind: "number",
    min: 0,
  },
  {
    name: "percentage",
    label: "Share (%)",
    kind: "number",
    min: 0,
    max: 100,
    step: "0.01",
  },
];

export const buildPlayerPositionPlayedCreateValues = () => ({
  team_id: "",
  sport_id: "",
  season: "",
  position: "",
  appearances: 0,
  percentage: 0,
});

export const buildPlayerPositionPlayedUpdateValues = (row: any) => ({
  team_id: row.team_id || "",
  sport_id: row.sport_id || "",
  season: row.season || "",
  position: row.position || "",
  appearances: row.appearances ?? 0,
  percentage: row.percentage ?? 0,
});

export const buildPlayerAchievementFields = ({
  teams,
  sports,
}: {
  teams: any[];
  sports: any[];
}): CrudFieldConfig[] => [
  {
    name: "team_id",
    label: "Team",
    kind: "select",
    options: toSelectOptions(teams, (team) => team.name || "Unnamed team"),
  },
  {
    name: "sport_id",
    label: "Sport",
    kind: "select",
    options: toSelectOptions(sports, (sport) => sport.name || "Unknown sport"),
  },
  {
    name: "title",
    label: "Title",
    placeholder: "Player of the tournament",
    required: true,
  },
  {
    name: "category",
    label: "Category",
    placeholder: "Award, milestone, record",
  },
  {
    name: "tournament",
    label: "Tournament",
    placeholder: "League final, continental cup",
  },
  {
    name: "season",
    label: "Season",
    placeholder: "2025/26",
  },
  {
    name: "year",
    label: "Year",
    kind: "number",
    min: 1900,
  },
  {
    name: "position",
    label: "Position",
    placeholder: "Forward, Wing, Goalkeeper",
  },
  {
    name: "level",
    label: "Level",
    placeholder: "Senior, U21, Club, National",
  },
  {
    name: "description",
    label: "Description",
    kind: "textarea",
    placeholder: "Add the detail behind this achievement.",
  },
];

export const buildPlayerAchievementCreateValues = () => ({
  team_id: "",
  sport_id: "",
  title: "",
  category: "",
  tournament: "",
  season: "",
  year: "",
  position: "",
  level: "",
  description: "",
});

export const buildPlayerAchievementUpdateValues = (row: any) => ({
  team_id: row.team_id || "",
  sport_id: row.sport_id || "",
  title: row.title || "",
  category: row.category || "",
  tournament: row.tournament || "",
  season: row.season || "",
  year: row.year || "",
  position: row.position || "",
  level: row.level || "",
  description: row.description || "",
});
